import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Snackbar,
  Alert,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Paper,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
} from "@mui/material";
import api from "../utils/axiosInstance";

export const CATEGORIES = [
  "Grocery Store",
  "Restaurant",
  "Toys",
  "Electronics",
  "Clothing",
  "Cafe",
  "Bakery",
  "Pharmacy",
  "Furniture",
  "Books",
  "Wines & Spirits",
  "Bicycle Shop",
  "Beauty & Cosmetics",
  "Shoe Shop",
  "Creatives Shop",
  "Household Store",
];

export const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function ShopInfo({ shop, onShopUpdate = () => {} }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [editing, setEditing] = useState(!shop);
  const [form, setForm] = useState({
    name: "",
    location: "",
    contact: "",
    latitude: "",
    longitude: "",
    description: "",
    category: "",
    open_time: "",
    close_time: "",
    is_24_hours: false,
    open_days: [],
    ...shop,
  });

  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [keyboardOffset, setKeyboardOffset] = useState(0); // 👈 keyboard offset state

  // --- Handle viewport changes for mobile keyboard ---
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleResize = () => {
      const offset = window.innerHeight - viewport.height - viewport.offsetTop;
      setKeyboardOffset(offset > 0 ? offset + 8 : 0);
    };

    viewport.addEventListener("resize", handleResize);
    viewport.addEventListener("scroll", handleResize);

    return () => {
      viewport.removeEventListener("resize", handleResize);
      viewport.removeEventListener("scroll", handleResize);
    };
  }, []);

  useEffect(() => {
    if (shop) {
      setForm((prev) => ({
        ...prev,
        ...shop,
        open_days: Array.isArray(shop.open_days)
          ? shop.open_days
          : typeof shop.open_days === "string"
          ? JSON.parse(shop.open_days || "[]")
          : [],
      }));
    }
  }, [shop]);

  useEffect(() => {
    if (!shop?.latitude || !shop?.longitude) getCurrentLocation();
  }, []);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const getCurrentLocation = () => {
    if (!navigator.geolocation) return;
    setLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm((prev) => ({
          ...prev,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }));
        setLoadingLocation(false);
      },
      (err) => {
        console.warn(err.message);
        setLoadingLocation(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const toggleDay = (day) => {
    setForm((prev) => {
      const newDays = prev.open_days.includes(day)
        ? prev.open_days.filter((d) => d !== day)
        : [...prev.open_days, day];
      return { ...prev, open_days: newDays };
    });
  };

  const handleSave = async () => {
    const required = ["name", "location", "contact", "latitude", "longitude"];
    if (!form.is_24_hours) required.push("open_time", "close_time");

    for (let field of required) {
      if (!form[field]) {
        setToast({
          open: true,
          message: `${field.replace("_", " ")} is required`,
          severity: "error",
        });
        return;
      }
    }

    setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (key === "open_days" && Array.isArray(value))
          formData.append(key, JSON.stringify(value));
        else if (key === "is_24_hours")
          formData.append(key, value ? "true" : "false");
        else formData.append(key, value);
      });
      if (logoFile) formData.append("image", logoFile);

      const res = form.id
        ? await api.put(`/shops/${form.id}`, formData)
        : await api.post("/shops", formData);

      setToast({
        open: true,
        message: `✅ Shop ${form.id ? "updated" : "created"}`,
        severity: "success",
      });
      onShopUpdate(res.data);
      setLogoFile(null);
      setPreview(null);
      setEditing(false);
    } catch (err) {
      console.error(err);
      setToast({ open: true, message: "❌ Save failed", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  // ---------- Summary View ----------
  if (!editing && shop) {
    return (
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          mt: 2,
          mx: "auto",
          maxWidth: 500,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6" mb={2} textAlign="center">
          {shop.name}
        </Typography>
        <Box textAlign="center" mb={2}>
          <img
            src={shop.image_url || "/placeholder-logo.png"}
            alt="Shop Logo"
            style={{
              width: isMobile ? 100 : 120,
              height: isMobile ? 100 : 120,
              borderRadius: 8,
              objectFit: "cover",
            }}
          />
        </Box>
        <Typography variant="body2">
          <strong>Category:</strong> {shop.category || "N/A"}
        </Typography>
        <Typography variant="body2">
          <strong>Location:</strong> {shop.location}
        </Typography>
        <Typography variant="body2">
          <strong>Contact:</strong> {shop.contact}
        </Typography>
        <Typography variant="body2">
          <strong>Description:</strong> {shop.description || "N/A"}
        </Typography>
        <Typography variant="body2">
          <strong>Working Hours:</strong>{" "}
          {shop.is_24_hours ? "24 Hours" : `${shop.open_time} - ${shop.close_time}`}
        </Typography>
        <Typography variant="body2">
          <strong>Open Days:</strong> {shop.open_days?.join(", ") || "N/A"}
        </Typography>
        <Box mt={3} textAlign="center">
          <Button
            variant="contained"
            size={isMobile ? "medium" : "large"}
            onClick={() => setEditing(true)}
          >
            Edit Shop
          </Button>
        </Box>
      </Paper>
    );
  }

  // ---------- Create / Edit Form ----------
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 600,
        mx: "auto",
        mt: { xs: 1, sm: 3 },
        p: { xs: 2, sm: 3 },
        pb: 10,
        bgcolor: "#fff",
        borderRadius: 2,
        boxShadow: 2,
        minHeight: "100vh",
        overflowY: "auto",
      }}
    >
      <Typography variant="h6" mb={2} textAlign="center">
        {form.id ? "Edit Shop" : "Create Your Shop"}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            label="Shop Name *"
            name="name"
            value={form.name}
            onChange={handleChange}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            select
            label="Category *"
            name="category"
            value={form.category}
            onChange={handleChange}
            fullWidth
          >
            {CATEGORIES.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Location *"
            name="location"
            value={form.location}
            onChange={handleChange}
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Contact *"
            name="contact"
            value={form.contact}
            onChange={handleChange}
            fullWidth
          />
        </Grid>

        {/* Coordinates + Location Button */}
        <Grid item xs={12} sm={4}>
          <TextField
            label="Latitude *"
            name="latitude"
            type="number"
            value={form.latitude}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="Longitude *"
            name="longitude"
            type="number"
            value={form.longitude}
            onChange={handleChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button
            onClick={getCurrentLocation}
            variant="outlined"
            fullWidth
            disabled={loadingLocation}
          >
            {loadingLocation ? <CircularProgress size={20} /> : "Use My Location"}
          </Button>
        </Grid>

        <Grid item xs={12}>
          <TextField
            label="Description"
            name="description"
            value={form.description}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={form.is_24_hours}
                onChange={(e) =>
                  setForm({ ...form, is_24_hours: e.target.checked })
                }
              />
            }
            label="Open 24 Hours"
          />
        </Grid>

        {!form.is_24_hours && (
          <>
            <Grid item xs={6}>
              <TextField
                label="Open Time *"
                name="open_time"
                type="time"
                value={form.open_time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Close Time *"
                name="close_time"
                type="time"
                value={form.close_time}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Typography variant="subtitle2">Open Days</Typography>
          <FormGroup row sx={{ flexWrap: "wrap" }}>
            {WEEK_DAYS.map((day) => (
              <FormControlLabel
                key={day}
                control={
                  <Checkbox
                    checked={form.open_days.includes(day)}
                    onChange={() => toggleDay(day)}
                  />
                }
                label={day}
              />
            ))}
          </FormGroup>
        </Grid>

        {/* Shop Logo */}
        <Grid item xs={12} textAlign="center">
          <Typography variant="subtitle2" mb={1}>
            Shop Logo
          </Typography>
          <img
            src={preview || form.image_url || "/placeholder-logo.png"}
            alt="Shop Logo"
            style={{
              width: isMobile ? 100 : 120,
              height: isMobile ? 100 : 120,
              borderRadius: 8,
              objectFit: "cover",
            }}
          />
          <Box mt={1}>
            <Button variant="outlined" component="label" size="small">
              Upload Logo
              <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Sticky Save Button */}
      <Box
        sx={{
          position: "fixed",
          bottom: keyboardOffset,
          left: 0,
          right: 0,
          bgcolor: "#fff",
          boxShadow: "0 -2px 6px rgba(0,0,0,0.1)",
          p: 2,
          transition: "bottom 0.3s ease",
          zIndex: 1200,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          fullWidth
          sx={{ py: 1.5 }}
        >
          {saving ? "Saving..." : form.id ? "Update Shop" : "Create Shop"}
        </Button>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.severity}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
}
